<template>
  <div class="shoe-detail">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <el-skeleton animated>
        <template #template>
          <div style="display: flex; flex-direction: column; gap: 20px;">
            <div style="display: flex; gap: 30px;">
              <el-skeleton-item variant="image" style="width: 500px; height: 500px; border-radius: 8px;" />
              <div style="flex: 1;">
                <el-skeleton-item variant="h1" style="width: 50%;" />
                <el-skeleton-item variant="text" style="margin-top: 20px; width: 30%;" />
                <el-skeleton-item variant="text" style="margin-top: 15px; width: 20%;" />
                <!-- Skeleton for purchase options -->
                <el-skeleton-item variant="text" style="margin-top: 30px; width: 100%; height: 20px" />
                <el-skeleton-item variant="text" style="margin-top: 10px; width: 100%; height: 20px" />
                <el-skeleton-item variant="text" style="margin-top: 10px; width: 80%; height: 20px" />
                <el-skeleton-item variant="text" style="margin-top: 10px; width: 60%; height: 20px" />
                <el-skeleton-item variant="button" style="margin-top: 20px; width: 40%; height: 40px" />
              </div>
            </div>
          </div>
        </template>
      </el-skeleton>
    </div>

    <!-- 错误提示 -->
    <el-alert
      v-if="error"
      :title="error"
      type="error"
      show-icon
      :closable="false"
      style="margin: 20px 0;"
    >
      <template #default>
        <div style="margin-top: 10px;">
          <el-button @click="fetchShoeData">重試</el-button>
          <el-button @click="goBack">返回</el-button>
        </div>
      </template>
    </el-alert>

    <!-- 鞋子详情内容 -->
    <div v-if="!loading && !error && shoe" class="shoe-content">
      <!-- 返回上一页按钮 -->
      <div class="back-button">
        <el-button @click="goBack" :icon="ElIconArrowLeft">返回</el-button> <!-- Pass icon component -->
      </div>

      <div class="detail-container">
        <!-- 图片轮播 -->
        <div class="image-gallery">
          <el-carousel ref="carousel" trigger="click" height="500px" indicator-position="outside" :autoplay="false" :initial-index="currentImageIndex">
            <el-carousel-item v-for="(image, index) in shoe.images" :key="index">
              <div class="carousel-image">
                <img :src="`${backendBaseUrl}/${image.url}`" :alt="`${shoe.title} - 圖片${index + 1}`" />
              </div>
            </el-carousel-item>
          </el-carousel>
          
          <!-- 图片缩略图 -->
          <div class="thumbnails">
            <div
              v-for="(image, index) in shoe.images"
              :key="index"
              class="thumbnail"
              :class="{ active: currentImageIndex === index }"
              @click="setActiveImage(index)"
            >
              <img :src="`${backendBaseUrl}/${image.url}`" :alt="`縮略圖${index + 1}`" />
            </div>
          </div>
        </div>

        <!-- 鞋子信息 & 购买选项 -->
        <div class="shoe-info-and-purchase">
          <div class="shoe-info">
            <h1 class="title"><t-text :text="shoe.title"></t-text></h1>
            
            <div class="model" v-if="shoe.model_number">
              <strong>型號:</strong> <t-text class="ignore" :text="shoe.model_number || '無型號'"></t-text>
            </div>

            <!-- Price is now shown in ProductPurchaseOptions, but can be kept here too if desired -->
            <div class="price price-normal" v-if="shoe.normal_price !== undefined && shoe.normal_price !== null">
              <strong>一般價:</strong>
              <span class="price-value ingnore">$ <t-text :text="shoe.normal_price.toLocaleString()"></t-text></span> TWD
            </div>

            <div class="price price-member" v-if="shoe.price !== undefined && shoe.price !== null">
              <strong>會員價:</strong>
              <span class="price-value ingnore">$ <t-text :text="shoe.price.toLocaleString()"></t-text></span> TWD
            </div>
           
            
            <div class="collections">
              <el-tag
                v-for="(collection, id) in shoe.tags"
                :key="id"
                size="medium"
              >
                <t-text :text="collection"></t-text>
              </el-tag>
            </div>

            <div class="description" v-if="shoe.detailed_description">
              <h3>詳細描述</h3>
              <p><t-text :text="shoe.detailed_description"></t-text></p>
            </div>
            
            <div class="html-description" v-if="shoe.description_html">
              <h3>描述</h3>
              <div v-html="convertHtmlToTraditional(shoe.description_html)"></div>
            </div>
          </div>
          
          <!-- 购买选项组件 -->
          <ProductPurchaseOptions
            v-if="shoe.title && discount_status"
            :brandName="shoe.brand || shoe.tags?.[0] || '未知品牌'" 
            :availableSkus="shoe.shoeSizes"
            :Fstatus="Fstatus"
            :price="shoe.price"
            :productId="shoe.id"
            :guarantees="current_discount"
            :isAuthenticated=isAuthenticated
            @addToCart="handleAddToCart"
            @buyNow="handleBuyNow"
            @toggleFavorite="handletoggleFavorite"
            class="purchase-options-component"
          />

        </div>
      </div>
    </div>

    <!-- 推荐的相关鞋子 -->
    <div v-if="relatedShoes.length > 0" class="related-shoes">
      <h2>相關商品</h2>
      <div class="related-grid">
        <div
          v-for="relatedShoe in relatedShoes"
          :key="relatedShoe.id"
          class="related-card"
          @click="goToShoe(relatedShoe.id)"
        >
          <div class="related-image">
            <img :src="getShoeImage(relatedShoe)" :alt="relatedShoe.title" />
          </div>
          <div class="related-info ignore">
            <h3><t-text :text="relatedShoe.title"></t-text></h3>
            <p><t-text :text="relatedShoe.model_number || '無型號'"></t-text></p>
            <p class="related-price" v-if="relatedShoe.price !== undefined && relatedShoe.price !== null">
              $ <t-text :text="relatedShoe.price.toLocaleString()"></t-text>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import CarService from '../../services/ShoppingCar'
import CollService from '../../services/CollectManage'
import discountServer from '../../services/discountManage';
import { convertToTraditional } from '@/utils/textConverter'
import { ElMessage } from 'element-plus'
import { ArrowLeft as ElIconArrowLeft } from '@element-plus/icons-vue'
import ProductPurchaseOptions from './components/ProductPurchaseOptions.vue' // 1. Import the new component

export default {
  name: 'ShoeDetailView',
  components: { // 2. Register the new component
    ProductPurchaseOptions,
    // ElIconArrowLeft // No need to register if passed directly as prop like :icon="ElIconArrowLeft"
  },
  setup() {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    
    const currentImageIndex = ref(0)
    const relatedShoes = ref([])
    const carousel = ref(null)
    const current_discount = ref([]);
    const discount_status = ref(false);

    const Fstatus = ref(false)
    const shoe = computed(() => store.getters['shoes/currentShoe'])
    const loading = computed(() => store.getters['shoes/isLoading'])
    const error = computed(() => store.getters['shoes/errorMessage'])
    const isAuthenticated = computed(() => store.getters['auth/authStatus'])
    const backendBaseUrl = process.env.VUE_APP_BACKEND_BASE_URL;

    const setActiveImage = (index) => {
      currentImageIndex.value = index
      if (carousel.value) {
        carousel.value.setActiveItem(index)
      }
    }
    
    const convertHtmlToTraditional = (html) => {
      if (!html) return '';
      return convertToTraditional(html);
    }
    
    const fetchShoeData = async () => {
      const shoeId = route.params.id
      if (shoeId) {
        await store.dispatch('shoes/fetchShoeDetail', shoeId)
        // Ensure shoe.value is populated before fetching related shoes
        if (shoe.value) {
          fetchRelatedShoes()
        }
      }
    }
    
    const fetchRelatedShoes = async () => {
      if (shoe.value && (shoe.value.tags && shoe.value.tags.length > 0 || shoe.value.brand)) {
        const queryParams = { page: 1, limit: 5, refresh: true, kind:shoe.value.kind };
        const result = await store.dispatch('shoes/fetchShoes', queryParams) 
        
        if (result && result.success && result.data) {
          relatedShoes.value = result.data.filter(s => s.id !== shoe.value.id).slice(0, 4)
        } else {
          relatedShoes.value = []
        }
      } else {
        relatedShoes.value = [];
      }
    }
    
    const getShoeImage = (shoeItem) => { 
      if (shoeItem.mainImage) {
        return `${backendBaseUrl}/${shoeItem.mainImage}`;
      }
      if (shoeItem.images && shoeItem.images.length > 0 && shoeItem.images[0].url) {
        return `${backendBaseUrl}/${shoeItem.images[0].url}`;
      }
      return 'https://via.placeholder.com/300x300?text=No+Image';
    }
    
    const goToCollection = (collectionId) => {
      router.push(`/collections/${collectionId}`)
    }
    
    const goToShoe = (shoeId) => {
      router.push(`/shoes/${shoeId}`)
    }
    
    const goBack = () => {
      router.back()
    }

    const handleAddToCart = async (payload) => {
      try{
        await CarService.addShoppingCar(payload)
        ElMessage.success('成功購物車')
      }catch(error){
        ElMessage.error(error);
        console.log(error)
      }
    };

    const handletoggleFavorite = async (payload) => {
      try{
        await CollService.changeColl(payload)
      }catch(error){
        ElMessage.error(error);
        console.log(error)
      }
    };

    
    const handleBuyNow = (payload) => {
      // payload 來自 ProductPurchaseOptions: { skuId, skuName, quantity, priceAtPurchase }

      if (!shoe.value || !shoe.value.id) {
        ElMessage.error('商品信息不完整，无法立即购买。');
        return;
      }

      if (!payload || !payload.skuId || payload.quantity <= 0) {
        ElMessage.error('购买选项无效，请重新选择。');
        return;
      }

      // 1. 準備 itemsToCheckout (只有一個商品)
      // 這裡的 ID 應該是購物車項目的 ID，但因為是直接購買，所以沒有購物車項目 ID。
      // 確認訂單頁的 :key 可能需要調整，或者我們傳遞一個臨時的唯一標識，
      // 或者讓 ConfirmOrderPage 的 :key 優先使用 productId。
      // 為了與購物車流程一致，我們盡量模擬 CartPage 傳遞的結構。
      // ConfirmOrderPage 期望的 item 結構: { id (購物車項ID), productId, name, image, sku, price, quantity }
      const itemToCheckout = {
        id: `direct_${shoe.value.id}_${payload.skuId}`, // 臨時唯一 ID，或設為 null/undefined
        productId: shoe.value.id,
        name: shoe.value.title,
        image: shoe.value.images && shoe.value.images.length > 0 ? shoe.value.images[0].url : (shoe.value.mainImage || ''), // 主圖或第一張圖
        sku: payload.skuName, // SKU 名稱 (例如 "42碼")
        normal_price: shoe.value.normal_price,
        price: payload.price, // 購買時的單價
        quantity: payload.quantity,
      };

      // 2. 計算 subtotalOriginal
      const subtotalOriginal = parseFloat((payload.price * payload.quantity).toFixed(2));
      const normal_subtotalOriginal = parseFloat((shoe.value.normal_price * payload.quantity).toFixed(2));

      // 3. 準備 appliedDiscountInfo
      let appliedDiscountInfo = null;
      let deductedAmount = 0;

      // current_discount.value 是一個包含描述字符串的數組，例如 ["夏季特惠: 10%off"]
      // 我們需要從中解析出折扣信息。假設折扣總是百分比，並且只有一個活動。
      // 這部分邏輯需要你根據 current_discount 的實際數據結構來完善。
      if (current_discount.value && current_discount.value.length > 0) {
        const discountString = current_discount.value[0]; // 假設只取第一個折扣
        // 嘗試從 "活动名称: XX%off" 中提取
        const match = discountString.match(/^(.*?):\s*([\d.]+)\s*%off$/i);
        if (match && match[1] && match[2]) {
          const discountName = match[1].trim();
          const discountRate = parseFloat(match[2]) / 100; // 轉換為小數，例如 0.1
          if (discountRate > 0 && subtotalOriginal > 0) {
            deductedAmount = subtotalOriginal * discountRate;
            appliedDiscountInfo = {
              name: discountName,
              rate: discountRate,
              deductedAmount: parseFloat(deductedAmount.toFixed(2)),
            };
          }
        } else {
          console.warn("無法從折扣字符串解析折扣信息:", discountString);
          // 如果 current_discount.value[0] 是固定的折扣金額，則直接使用
          // 例如，如果它是 "满100减20"，你需要不同的解析邏輯
        }
      }


      // 4. 運費 (暫設為0，你可以根據實際邏輯調整)
      const shippingFeeForCheckout = 0; // 或者從某處獲取

      // 5. 計算 totalAmount
      let totalAmount = subtotalOriginal - (appliedDiscountInfo ? appliedDiscountInfo.deductedAmount : 0);
      totalAmount += shippingFeeForCheckout;
      totalAmount = Math.max(0, parseFloat(totalAmount.toFixed(2)));

      let normal_totalAmount = normal_subtotalOriginal - (appliedDiscountInfo ? appliedDiscountInfo.deductedAmount : 0);
      normal_totalAmount = Math.max(0, parseFloat(normal_totalAmount.toFixed(2)));

      // 6. 打包所有數據
      const checkoutDataForConfirm = {
        itemsToCheckout: [itemToCheckout],
        subtotalOriginal: subtotalOriginal,
        normal_subtotalOriginal: normal_subtotalOriginal,
        appliedDiscountInfo: appliedDiscountInfo,
        shippingFee: shippingFeeForCheckout,
        totalAmount: totalAmount,
        normal_totalAmount: normal_totalAmount,
        notes: '', // "立即購買" 通常沒有全局備註，確認頁可以填
        // selectedAddressId: null, // 地址選擇在 ConfirmOrderPage 處理
      };

      // 8. 導航到確認訂單頁面
      router.push({
        name: 'CheckOrder', // 確認訂單頁的路由名
        state: { checkoutDataForConfirm: checkoutDataForConfirm }
      });
    };
    
    watch(() => route.params.id, (newId, oldId) => {
      if (newId && newId !== oldId) {
        currentImageIndex.value = 0;
        fetchShoeData()
      }
    })
    
    onMounted(async () => {
      const result = await discountServer.getCurrentCampaign();
      if (Object.keys(result).length > 0) {
        const offvalue = result.discountValue * 100;
        const discountText = `${result.name}: ${offvalue}%off`;
        current_discount.value.push(discountText);
      }
      discount_status.value = true
      fetchShoeData()
      if (isAuthenticated.value){
        Fstatus.value = await CollService.getCollstatus(route.params.id)
      }
    })
    
    return {
      shoe,
      loading,
      error,
      currentImageIndex,
      relatedShoes,
      carousel,
      fetchShoeData,
      getShoeImage,
      goToCollection,
      goToShoe,
      goBack,
      setActiveImage,
      convertHtmlToTraditional,
      backendBaseUrl,
      ElIconArrowLeft, // Expose icon for template
      handleAddToCart, // Expose handlers
      handletoggleFavorite,
      handleBuyNow,
      isAuthenticated,
      Fstatus,
      current_discount,
      discount_status
    }
  }
}
</script>

<style scoped>
.shoe-detail {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.loading-container {
  margin: 40px 0;
}

.back-button {
  margin-bottom: 20px;
}
.back-button .el-button {
  display: inline-flex;
  align-items: center;
}
/* Icon margin is handled by Element Plus if :icon prop is used */


.detail-container {
  display: flex;
  flex-wrap: wrap;
  gap: 40px;
  margin-bottom: 40px;
}

.image-gallery {
  flex: 1;
  min-width: 300px; 
  max-width: 550px; 
}

.carousel-image {
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f9f9f9;
  border-radius: 8px;
  overflow: hidden;
}

.carousel-image img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.thumbnails {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 15px;
  justify-content: center; 
}

.thumbnail {
  width: 80px;
  height: 80px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  transition: border-color 0.3s, transform 0.2s, opacity 0.2s;
  opacity: 0.7;
}

.thumbnail:hover {
  border-color: #409eff;
  opacity: 0.9;
  transform: translateY(-2px);
}

.thumbnail.active {
  border-color: #409eff;
  opacity: 1;
  transform: scale(1.05) translateY(-1px);
}

.thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* New wrapper for shoe info and purchase options */
.shoe-info-and-purchase {
  flex: 1.2;
  min-width: 320px; /* Slightly more min-width */
  display: flex;
  flex-direction: column;
  gap: 25px; /* Space between original info and new purchase component */
}

.purchase-options-component {
  margin-top: 10px; /* Or adjust gap in .shoe-info-and-purchase */
}


.title {
  font-size: 2rem;
  color: #333;
  margin: 0 0 15px 0;
  line-height: 1.3;
}

.price {
  margin: 15px 0 5px;
  display: flex;
  align-items: baseline; /* 讓 "標籤" 和 "價格" 的基線對齊，看起來更專業 */
  gap: 8px; /* 在標籤和價格之間創建一些間距 */
}

.price strong {
  font-weight: 500;
  font-size: 1rem;
}

.price .price-value {
  font-weight: 600;
}

/* 一般價樣式 (不突出，通常會有刪除線) */
.price-normal {
  color: #909399; /* 使用灰色文字 */
  font-size: 1rem;
}

/* 會員價樣式 (重點突出) */
.price-member {
  color: #303133; /* 標籤文字使用深色 */
}

.price-member .price-value {
  color: #e4393c; /* 使用和相關商品一樣的醒目紅色 */
  font-weight: bold;
}

.model {
  margin: 10px 0 15px; 
  color: #666;
  font-size: 1.1rem;
}

.collections {
  margin: 20px 0;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.description, .html-description {
  margin-top: 30px;
}

.description h3, .html-description h3 {
  font-size: 1.3rem;
  color: #333;
  margin-bottom: 10px;
}

.description p {
  color: #666;
  line-height: 1.7; 
}

.html-description {
  line-height: 1.7; 
}
.html-description :deep(p) { 
  margin-bottom: 1em;
}
.html-description :deep(img) { 
  max-width: 100%;
  height: auto;
  border-radius: 4px;
}


.related-shoes {
  margin-top: 50px;
  padding-top: 30px;
  border-top: 1px solid #eee;
}

.related-shoes h2 {
  font-size: 1.8rem;
  color: #333;
  margin-bottom: 20px;
  text-align: center; 
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); 
  gap: 20px;
}

.related-card {
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.08); 
  transition: transform 0.3s, box-shadow 0.3s;
  cursor: pointer;
  display: flex;
  flex-direction: column;
}

.related-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.12);
}

.related-image {
  height: 180px; 
  overflow: hidden;
  background-color: #f9f9f9;
}

.related-image img {
  width: 100%;
  height: 100%;
  object-fit: cover; 
  transition: transform 0.3s;
}

.related-card:hover .related-image img {
  transform: scale(1.05);
}

.related-info {
  padding: 15px;
  flex-grow: 1; 
  display: flex;
  flex-direction: column;
}

.related-info h3 {
  margin: 0 0 8px 0; 
  font-size: 1rem; 
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.related-info p {
  color: #777; 
  font-size: 0.9rem; 
  margin: 0 0 5px 0;
}
.related-price {
  color: #e4393c;
  font-weight: bold;
  font-size: 1rem;
  margin-top: auto; 
}


@media (max-width: 992px) { 
  .detail-container {
    gap: 30px; 
  }
  .image-gallery {
    max-width: 100%; 
  }
}

@media (max-width: 768px) {
  .detail-container {
    flex-direction: column;
  }
  .title {
    font-size: 1.8rem; 
  }
  /*
  .price {
    font-size: 1.3rem; 
  }
  */
  .related-grid {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); 
  }
}
</style>